export const navigation = [
  { name: 'Product Identity', href: '/product/product-identity' },
  { name: 'DPP Compliance', href: '/product/digital-product-passport' },
  { name: 'Post-Purchase', href: '/product/post-purchase-experience' },
  { name: 'Pricing', href: '/#pricing' },
  { name: 'Blog', href: '/blog/' },
  { name: 'Tools', href: '/tools/dpp-scorecard.html' },
];

export const features = [
  {
    name: 'QR & NFC Tags',
    description:
      'Turn packaging into a customer acquisition channel with branded QR codes and NFC stickers that link directly to product experiences.',
    icon: 'QrCodeIcon',
    slug: 'qr-nfc-tags',
  },
  {
    name: 'Product Experiences',
    description:
      'Mobile-friendly product pages with manuals, videos, and guided setup. No app download needed — everything runs in the browser.',
    icon: 'DevicePhoneMobileIcon',
    slug: 'product-experiences',
  },
  {
    name: 'Warranty Activation & Claims',
    description:
      'Instant digital registration replaces paper cards. Customers activate warranties in seconds, and you manage claims end-to-end.',
    icon: 'ShieldCheckIcon',
    slug: 'warranty',
  },
  {
    name: 'AI Support Agent',
    description:
      'Trained on your product manuals, resolves queries instantly with grounded retrieval. Customers get answers in under 30 seconds.',
    icon: 'SparklesIcon',
    slug: 'ai-support',
  },
  {
    name: 'Spares & Accessories Sales',
    description:
      'Surface the right parts at the right moment. Capture aftermarket revenue that currently leaks to third-party sellers.',
    icon: 'ShoppingCartIcon',
    slug: 'spares-accessories',
  },
  {
    name: 'Returns & Repairs',
    description:
      'Track repair requests, manage returns, and assign work to service centers — all visible to the customer in real time.',
    icon: 'WrenchIcon',
    slug: 'returns-repairs',
  },
  {
    name: 'Compliance & DPP',
    description:
      'Host CE, RoHS, DPP, and ESPR documents per product. GS1 Digital Link resolution for EU Digital Product Passport compliance.',
    icon: 'DocumentCheckIcon',
    slug: 'compliance-dpp',
  },
  {
    name: 'Analytics & Customers',
    description:
      'Track scans, registrations, and engagement across your product range. View and manage your customer base with filtered dashboards.',
    icon: 'ChartBarIcon',
    slug: 'analytics',
  },
];

export const tiers = [
  {
    name: 'Starter',
    id: 'tier-starter',
    href: '#',
    priceMonthly: '$99',
    priceAnnual: '$79',
    tagline: '500 tags/mo included',
    overage: '$0.15/additional tag',
    description: 'For brands getting started with connected products. Up to 500 serialised tags per month.',
    features: [
      'Serialised QR tags (GS1 Digital Link)',
      'Product experiences (setup, manuals, videos)',
      'Warranty registration',
      'DPP compliance data hosting',
      'Basic analytics (scans, registrations)',
      'Customer management',
      'Unlimited scans',
      '2 team members',
    ],
    mostPopular: false,
  },
  {
    name: 'Growth',
    id: 'tier-growth',
    href: '#',
    priceMonthly: '$299',
    priceAnnual: '$239',
    tagline: '5,000 tags/mo included',
    overage: '$0.10/additional tag',
    description: 'For brands scaling post-purchase operations. Full warranty, AI support, and commerce.',
    features: [
      'Everything in Starter, plus:',
      'Warranty claims management',
      'AI support agent ($0.02/interaction)',
      'Returns & repairs tracking',
      'Spare parts commerce (3% GMV)',
      'Installation tracking & recalls',
      'Advanced analytics + CSV export',
      'API & MCP access',
      'NFC tags',
      'GS1 Digital Links',
      '10 team members',
    ],
    mostPopular: true,
  },
  {
    name: 'Scale',
    id: 'tier-scale',
    href: '#',
    priceMonthly: '$799',
    priceAnnual: '$639',
    tagline: '25,000 tags/mo included',
    overage: '$0.06/additional tag',
    description: 'For brands with large product ranges. White-label, priority support, and full API access.',
    features: [
      'Everything in Growth, plus:',
      'AI support (500 free interactions/mo)',
      'Spare parts commerce (2% GMV)',
      'White-label product experiences',
      'Priority support',
      'Advanced analytics + API',
      '25 team members',
    ],
    mostPopular: false,
  },
];

export const faqs = [
  {
    id: 1,
    question: 'Do my customers need to download an app?',
    answer:
      'No. BrandedMark is entirely browser-based. Customers scan a QR code or tap an NFC tag and instantly access product experiences, support, and warranty registration — no app store, no downloads, no friction.',
  },
  {
    id: 2,
    question: 'What types of products work with BrandedMark?',
    answer:
      'Any physical product. From consumer electronics and home appliances to furniture, fitness equipment, and outdoor gear — if it ships in a box, BrandedMark can enhance the experience.',
  },
  {
    id: 3,
    question: 'How does the AI support agent work?',
    answer:
      'Our AI agent is trained on your product manuals and documentation. It uses grounded retrieval to provide accurate, product-specific answers instantly — reducing support call volume and resolution times.',
  },
  {
    id: 4,
    question: 'What about EU Digital Product Passport compliance?',
    answer:
      'BrandedMark includes built-in GS1 Digital Link resolution and compliance document hosting. You can publish CE, RoHS, DPP, and ESPR documentation per product to meet regulatory requirements.',
  },
  {
    id: 5,
    question: 'How quickly can I get started?',
    answer:
      'Days, not months. Upload your product data, generate QR codes, and start connecting with customers. No complex integration required — though we support Shopify, WooCommerce, and custom APIs when you need them.',
  },
  {
    id: 6,
    question: 'What integrations do you support?',
    answer:
      'We integrate with Shopify, WooCommerce, and provide a full REST API and MCP access for custom integrations. Connect BrandedMark to your existing e-commerce, CRM, or ERP systems.',
  },
  {
    id: 7,
    question: 'What is a "tag" and how does pricing work?',
    answer:
      'A tag is a unique serialised QR code for one product unit. You pay a platform fee plus per-tag overage beyond your plan\'s included tags. Tags never expire — your product\'s digital identity is permanent, even if you cancel.',
  },
  {
    id: 8,
    question: 'Do QR codes expire if I cancel?',
    answer:
      'No. BrandedMark QR codes are permanent. Unlike other platforms that kill codes when subscriptions lapse, your product identities continue to resolve. We believe your customers shouldn\'t suffer for a billing decision.',
  },
];

export const footerNavigation = {
  product: [
    { name: 'QR & NFC Tags', href: '/product/qr-nfc-tags' },
    { name: 'Product Experiences', href: '/product/product-experiences' },
    { name: 'Warranty', href: '/product/warranty' },
    { name: 'AI Support', href: '/product/ai-support' },
    { name: 'Compliance & DPP', href: '/product/compliance-dpp' },
  ],
  resources: [
    { name: 'Blog', href: '/blog/' },
    { name: 'Sitemap', href: '/sitemap.xml' },
  ],
  company: [
    { name: 'About', href: '#' },
    { name: 'Contact', href: 'mailto:hello@brandedmark.com' },
  ],
  legal: [
    { name: 'Legal', href: '/legal/' },
    { name: 'Privacy Policy', href: '/legal/privacy/' },
    { name: 'Cookie Policy', href: '/legal/cookies/' },
    { name: 'Accessibility', href: '/legal/accessibility/' },
  ],
};
